/* eslint-disable no-console */
// Using example of documentation in https://tanstack.com/query/latest/docs/framework/react/plugins/persistQueryClient#building-a-persister as a base
import { get, set, del } from 'idb-keyval'
import { QueryKey } from '@tanstack/react-query'
import {
  PersistedClient,
  Persister,
} from '@tanstack/react-query-persist-client'

export const IDB_VALID_KEY_PREFIX = 'reactQuery'

/**
 * Creates an Indexed DB persister
 * @see https://developer.mozilla.org/en-US/docs/Web/API/IndexedDB_API
 */
export function createIDBPersister() {
  const combinedKey = `${IDB_VALID_KEY_PREFIX}-${
    import.meta.env.VITE_ENVIRONMENT
  }`
  /**
   * Wrapper function to execute an IndexedDB operation safely.
   * Catches critical IndexedDB errors, logs them, attempts cleanup,
   * and returns 'undefined' to signal a non-restorable state.
   * @param fn The IndexedDB operation (get, set, or del)
   * @returns The result of the operation, or undefined on critical failure.
   */
  async function safe<T>(
    fn: () => Promise<T | undefined>,
  ): Promise<T | undefined> {
    try {
      return await fn()
    } catch (err) {
      // Errors indicating corrupted DB or quota failure
      if (
        err instanceof DOMException &&
        [
          'UnknownError',
          'QuotaExceededError',
          'InvalidStateError',
          'DataError',
        ].includes(err.name)
      ) {
        console.warn(
          `Caught critical IndexedDB error (${err.name}). Attempting to clear the store key:`,
          combinedKey,
        )
        try {
          // Self-Healing: Attempt to delete the corrupt key/data
          await del(combinedKey)
        } catch (cleanupErr) {
          console.error('Failed to clear corrupted IndexedDB key:', cleanupErr)
        }
        return
      }
      throw err
    }
  }

  return {
    persistClient: (client: PersistedClient) =>
      safe(async () => {
        try {
          await set(combinedKey, client)
        } catch (err) {
          // Specific handling for non-serializable data
          if (err instanceof DOMException && err.name === 'DataCloneError') {
            console.warn(
              'Failed to persist non-cloneable data, skipping this persistence cycle:',
              err,
            )
            return
          }
          // Throw other errors so they are caught by the outer 'safe' wrapper
          throw err
        }
      }),

    restoreClient: async () => safe(() => get<PersistedClient>(combinedKey)),
    removeClient: async () => {
      try {
        await del(combinedKey)
      } catch (cleanupErr) {
        console.error(
          'Failed to clear corrupted IndexedDB key when removing client:',
          cleanupErr,
        )
      }
    },
  } satisfies Persister
}

export const PERSISTED_TIME = 1000 * 60 * 60 * 12 // 12 hours

export function isQueryPersisted(queryKey: QueryKey): boolean {
  const lastElement = queryKey[queryKey.length - 1]
  if (
    lastElement &&
    typeof lastElement === 'object' &&
    'persist' in lastElement
  )
    return !!lastElement.persist

  return false
}
